import axios from "@/utils/axios";
import type { FormattedForm } from "@/workflow/components/configurations/form/form-data-formatter";
import { useToast } from "@elaniintc/goes-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  resolveFormValidationError,
  type ValidationError,
} from "./error-handling";

// save form
export const useCreateForm = () => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { form: FormattedForm }) => {
      const response = await axios.post("/admin/v1/forms", { ...data.form });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-forms"] });
      toast.showToast({
        title: "Formulario creado",
        description: "El formulario ha sido creado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al crear formulario";
      let description = "Ocurrió un error al intentar crear el formulario.";

      if (error?.response?.status === 422) {
        ({ title, description } = resolveFormValidationError(
          error?.response?.data?.errors,
        ));
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const getForm = async (id: string) => {
  const response = await axios.get(`/admin/v1/forms/${id}`);
  return response.data;
};

// get form by id
export const useGetForm = (id: string) => {
  return useQuery({
    queryKey: ["admin-form", id],
    queryFn: async () => {
      const response = await axios.get(`/admin/v1/forms/${id}`);
      return response.data;
    },
    enabled: !!id, // only runs the query when an id exists
  });
};

export const useUpdateForm = () => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { form: FormattedForm }) => {
      const response = await axios.put(`/admin/v1/forms/${data.form.id}`, {
        ...data.form,
      });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-forms"] });
      toast.showToast({
        title: "Formulario actualizado",
        description: "El formulario ha sido actualizado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al actualizar el formulario";
      let description =
        "Ocurrió un error al intentar actualizar el formulario.";

      if (error?.response?.status === 422) {
        ({ title, description } = resolveFormValidationError(
          error?.response?.data?.errors,
        ));
      } else if (error?.response?.status === 404) {
        title = "Formulario no encontrado";
        description = "El formulario que intentas actualizar no existe.";
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};
